import { useState, useRef, useEffect } from 'react';
import { isWindows, isMacOs, osVersion, isSafari } from 'react-device-detect';
import { useTranslation, Trans} from 'react-i18next';
import { Row, Col, Modal } from 'antd';

import './system-check.less';
import * as Result from './util/system-check-result';
import Webcam from './webcam';
import Browser from './browser';
import ProButton from '../../shared/pro-button/pro-button';
import appInsights from '../../../lib/app-insights';
import SystemCheckProgress from './system-check-progress';
import WaveForm from './util/wave-form';
import DownloadSpeed from './download-speed';
import UploadSpeed from './upload-speed';
import OctagonExclamation from '../../../assets/octagon-exclamation.svg';

interface IMicrophone {
    id: string,
    label: string
}
let WebcamStatus = {
    notStarted: true,
    inProgress: false,
    passed: false,
    failed: false,
    completed: false,
}
let BrowserStatus = {
    notStarted: true,
    inProgress: false,
    passed: false,
    failed: false,
    completed: false,
}
let source: MediaElementAudioSourceNode;
let audioCtx: AudioContext;
let typeAny: any;
let AudioData = {
    analyzer: typeAny,
    bufferLength: typeAny,
    dataArray: typeAny
};

enum SystemCheckStatus {
    NOT_STARTED = 'Not Started',
    IN_PROGRESS = 'In Progress',
    PASSED = 'Passed',
    WARNING = 'Warning',
    FAILED = 'Failed',
    UNKNOWN = 'Unknown'
}


function SystemCheck(props: any) {
    const { t } = useTranslation();
    const [systemCheckStart, setSystemCheckStart] = useState(false);
    const [analyzerData, setAnalyzerData] = useState<any>(null);
    const [minimumOSVersion, setMinimumOSVersion] = useState('');
    const [currentOSVersion, setCurrentOSVersion] = useState('');
    const [osVersionCheckNotStarted, setOSVersionCheckNotStarted] = useState(true);
    const [osVersionCheckInProgress, setOSVersionCheckInProgress] = useState(false);
    const [osVersionCheckPassed, setOSVersionCheckPassed] = useState(false);
    const [osVersionCheckFailed, setOSVersionCheckFailed] = useState(false);
    const [osVersionCheckWarning, setOSVersionCheckWarning] = useState(false);
    const [osVersionCheckErrorMessage, setOSVersionCheckErrorMessage] = useState('');

    const [minimumScreenResolution, setMinimumScreenResolution] = useState('');
    const [recommendedScreenResolution, setRecommendedScreenResolution] = useState('');
    const [currentScreenResolution, setCurrentScreenResolution] = useState('');
    const [screenResolutionCheckNotStarted, setScreenResolutionCheckNotStarted] = useState(true);
    const [screenResolutionCheckInProgress, setScreenResolutionCheckInProgress] = useState(false);
    const [screenResolutionCheckPassed, setScreenResolutionCheckPassed] = useState(false);
    const [screenResolutionCheckFailed, setScreenResolutionCheckFailed] = useState(false);
    const [screenResolutionCheckErrorMessage, setScreenResolutionCheckErrorMessage] = useState('');

    const [availableMicrophones, setAvailableMicrophones] = useState<IMicrophone[] | null>(null);
    const [multipleMicrophonesAvailable, setMultipleMicrophonesAvailable] = useState(false);
    const [selectedMicrophoneName, setSelectedMicrophoneName] = useState('');
    const [microphoneCheckNotStarted, setMicrophoneCheckNotStarted] = useState(true);
    const [microphoneCheckInProgress, setMicrophoneCheckInProgress] = useState(false);
    const [microphoneCheckComplete, setMicrophoneCheckComplete] = useState(false);
    const [microphoneCheckPassed, setMicrophoneCheckPassed] = useState(false);
    const [microphoneCheckFailed, setMicrophoneCheckFailed] = useState(false);
    const [microphoneCheckErrorMessage, setMicrophoneCheckErrorMessage] = useState('');
    const [enableMicrophoneRecordOption, setEnableMicrophoneRecordOption] = useState(false);
    const [enableMicrophoneStopOption, setEnableMicrophoneStopOption] = useState(false);
    const [enableMicrophonePlaybackOption, setEnableMicrophonePlaybackOption] = useState(false);
    const [enableMicrophoneSelectOption, setEnableMicrophoneSelectOption] = useState(false);
    const [displayMicrophoneCheckErrorMessage, setDisplayMicrophoneCheckErrorMessage] = useState(false);

    const [webcamStatus, setWebcamStatus] = useState(WebcamStatus);
    const [browserCheckStatus, setBrowserCheckStatus] = useState(BrowserStatus);

    const mediaRecorder = useRef<any>(null);
    const audioElmRef = useRef<any>(null);
    const [audioStream, setAudioStream] = useState<any | null>(null);
    const [audioChunks, setAudioChunks] = useState<any>([]);
    const [audio, setAudio] = useState('');
    const [playAudio, setPlayAudio] = useState(false);
    const mimeTypeWebm = "audio/webm";
    const mimeTypeMp4 = "audio/mp4";

    const checkWebcam = () => {
        WebcamStatus.notStarted = false;
        WebcamStatus.inProgress = true;
        WebcamStatus.failed = false;
        WebcamStatus.passed = false;
        WebcamStatus.completed = false;
        setWebcamStatus(WebcamStatus);
    }

    const [recommendedDownloadSpeed, setRecommendedDownloadSpeed] = useState('');
    const [currentDownloadSpeed, setCurrentDownloadSpeed] = useState('');
    const [downloadSpeedCheckNotStarted, setDownloadSpeedCheckNotStarted] = useState(true);
    const [downloadSpeedCheckInProgress, setDownloadSpeedCheckInProgress] = useState(false);
    const [downloadSpeedCheckPassed, setDownloadSpeedCheckPassed] = useState(false);
    const [downloadSpeedCheckFailed, setDownloadSpeedCheckFailed] = useState(false);
    const [downloadSpeedCheckErrorMessage, setDownloadSpeedCheckErrorMessage] = useState('');

    const [recommendedUploadSpeed, setRecommendedUploadSpeed] = useState('');
    const [currentUploadSpeed, setCurrentUploadSpeed] = useState('');
    const [uploadSpeedCheckNotStarted, setUploadSpeedCheckNotStarted] = useState(true);
    const [uploadSpeedCheckInProgress, setUploadSpeedCheckInProgress] = useState(false);
    const [uploadSpeedCheckPassed, setUploadSpeedCheckPassed] = useState(false);
    const [uploadSpeedCheckFailed, setUploadSpeedCheckFailed] = useState(false);
    const [uploadSpeedCheckErrorMessage, setUploadSpeedCheckErrorMessage] = useState('');

    const [systemCheckDone, setSystemCheckDone] = useState(false);

    const delay = parseInt(process.env.REACT_APP_SYSTEM_CHECK_FAKE_DELAY as string); //In milliseconds

    useEffect(() => {
        if (uploadSpeedCheckFailed || uploadSpeedCheckPassed) {
            setTimeout(() => setSystemCheckDone(true), delay);
        }
    }, [uploadSpeedCheckPassed, uploadSpeedCheckFailed]);

    const [webcamCurrentResolution, setWebcamCurrentResolution] = useState('');
    const [webcamName, setWebcamName] = useState('');
    const [webcamCheckErrorMessage, setWebcamCheckErrorMessage] = useState('');    
    
    const startSystemCheck = async () => {
        appInsights.logInfo('System Check Started');
        setSystemCheckStart(true);
        resetStates();
        resetWebcamStatus();
        await navigator.mediaDevices?.getUserMedia({
            audio: true
        });

        if (props.isCheckBrowser) {
            setBrowserCheckStatus({
                notStarted: false,
                inProgress: true,
                passed: false,
                failed: false,
                completed: false,
            });
        } else {
            await checkOS();
        }
        await timeout(delay);
        await checkScreenResolution();
        await timeout(delay);
        await checkMicrophone();
        appInsights.logInfo('System Check Completed');
    }

    const timeout = (delay: number) => {
        return new Promise(res => setTimeout(res, delay));
    }

    const checkOS = async () => {
        try {
            appInsights.logInfo('OS System Check Started.');
            setOSVersionCheckNotStarted(false);
            setOSVersionCheckInProgress(true);

            let windowsMinimumVersion = process.env.REACT_APP_WIN_MIN_VERSION as string;
            let macMinimumVersion = process.env.REACT_APP_MAC_MIN_VERSION as string;
            let macExcludedVersionMin = process.env.REACT_APP_MAC_MIN_EXCLUDED_VERSION as string;
            let macExcludedVersionMax = process.env.REACT_APP_MAC_MAX_EXCLUDED_VERSION as string;
            let macExcludedVersionOther = process.env.REACT_APP_MAC_OTHER_EXCLUDED_VERSION as string;

            let osVersionCheckPass = false;
            let osVersionCheckWarning = false;
            let checkMacExcludedVersionOther = false;
            
            if (process.env.REACT_APP_MAC_CHECK_OTHER_EXCLUDED_VERSION?.toLocaleLowerCase() === 'true') {
                checkMacExcludedVersionOther = true;
            }

            macExcludedVersionOther = checkMacExcludedVersionOther ? `${t('AND')} ${macExcludedVersionOther}` : "";

            let minimumOSVersion = `${t('SYSTEM_CHECK_OPERATING_SYSTEM_MINIMUM_VERSION', { windowsVersion: windowsMinimumVersion, macVersion: macMinimumVersion })} 
                ${t('HEADER_MAC_RANGE_VERSION', { macExcludedVersionMin: macExcludedVersionMin, macExcludedVersionMax: macExcludedVersionMax, macExcludedVersionOther: macExcludedVersionOther })}`;

            appInsights.logInfo('Minimum OS Version: ' + minimumOSVersion);
            setMinimumOSVersion(minimumOSVersion);

            await timeout(delay);

            let osVersionFormatted = osVersion.replaceAll('_', '.');
            let [currentMajor, currentMinor = 0, currentPatch = 0] = osVersionFormatted.split('.').map(s => parseInt(s, 10));
            let userAgentDataSupported = false;

            if (navigator.userAgentData) {
                await navigator.userAgentData.getHighEntropyValues(["platformVersion"])
                    .then(ua => {
                        if (navigator.userAgentData) {
                            userAgentDataSupported = true;

                            if (ua.platformVersion) {
                                const majorPlatformVersion = parseInt(ua.platformVersion.split('.')[0]);
                                if (navigator.userAgentData.platform === "Windows" && majorPlatformVersion >= 13) {
                                    currentMajor = 11;
                                    osVersionFormatted = "11";
                                }
                                else if (navigator.userAgentData.platform === "macOS") {
                                    [currentMajor, currentMinor = 0, currentPatch = 0] = ua.platformVersion.split('.').map(s => parseInt(s, 10));
                                    osVersionFormatted = ua.platformVersion;
                                }
                            }
                        }
                    });
            }

            if (isWindows) {
                if (userAgentDataSupported === false && osVersionFormatted === "10") {
                    osVersionFormatted = "10/11";
                }

                let currentOSVersion = t('SYSTEM_CHECK_OPERATING_SYSTEM_WINDOWS_VERSION', { windowsVersion: osVersionFormatted });
                appInsights.logInfo('Current Windows OS Version: ' + currentOSVersion);
                setCurrentOSVersion(currentOSVersion);
                const [minimumMajor, minimumMinor = 0, minimumPatch = 0] = windowsMinimumVersion.split('.').map(s => parseInt(s, 10));

                if (currentMajor > minimumMajor ||
                    (currentMajor === minimumMajor && currentMinor > minimumMinor) ||
                    (currentMajor === minimumMajor && currentMinor === minimumMinor && currentPatch >= minimumPatch)) {
                    osVersionCheckPass = true;
                }
            }
            else if (isMacOs) {
                if (userAgentDataSupported === false) {
                    osVersionCheckWarning = true;
                }

                let currentOSVersion = t('SYSTEM_CHECK_OPERATING_SYSTEM_MAC_VERSION', { macVersion: osVersionFormatted });
                appInsights.logInfo('Current Mac OS Version: ' + currentOSVersion);
                setCurrentOSVersion(currentOSVersion);
                const [minimumMajor, minimumMinor = 0, minimumPatch = 0] = macMinimumVersion.split('.').map(s => parseInt(s, 10));

                let unsupportedOSVersion = false;
                let macMinExcludedVersion = process.env.REACT_APP_MAC_MIN_EXCLUDED_VERSION as string;
                let macMaxExcludedVersion = process.env.REACT_APP_MAC_MAX_EXCLUDED_VERSION as string;
                let macOtherExcludeVersion = process.env.REACT_APP_MAC_OTHER_EXCLUDED_VERSION as string;

                let [minExcludedMajor, minExcludedMinor = 0, minExcludedPatch = 0] = macMinExcludedVersion.split('.').map(s => parseInt(s, 10));
                let [maxExcludedMajor, maxExcludedMinor = 0, maxExcludedPatch = 0] = macMaxExcludedVersion.split('.').map(s => parseInt(s, 10));
                let [otherExcludedMajor, otherExcludedMinor = 0, otherExcludedPatch = 0] = macOtherExcludeVersion.split('.').map(s => parseInt(s, 10));

                // Checking for the minimum & maximum macOS versions exclude
                let isWithinExcludedMajor = currentMajor > minExcludedMajor && currentMajor < maxExcludedMajor;
                let isOnLowerBound = currentMajor === minExcludedMajor && currentMinor >= minExcludedMinor && currentPatch >= minExcludedPatch;
                let isOnUpperBound = currentMajor === maxExcludedMajor && currentMinor <= maxExcludedMinor && currentPatch <= maxExcludedPatch;
                let isOnMinAndBelowMax = currentMajor === minExcludedMajor && currentMajor !== maxExcludedMajor && (currentMinor > minExcludedMinor || (currentMinor === minExcludedMinor && currentPatch >= minExcludedPatch));
                let isOnMaxAndAboveMin = currentMajor === maxExcludedMajor && currentMajor !== minExcludedMajor && (currentMinor < maxExcludedMinor || (currentMinor === maxExcludedMinor && currentPatch <= maxExcludedPatch));

                /// Checking for the specific macOS version exclude. Ex: macOS Sonoma (14.0)
                /// For now, we assume that the App unsupported Sonoma so we've just checked only the major number
                let isOtherExcludeMajor = currentMajor === otherExcludedMajor;

                /// Checking one of the conditions above is TRUE
                /// Flag [unsupportedOSVersion] turn on
                if (isWithinExcludedMajor || (isOnLowerBound && isOnUpperBound) || isOnMinAndBelowMax || isOnMaxAndAboveMin) {
                    unsupportedOSVersion = true;
                }

                /// If check other version is true, check isOtherExcludeMajor
                if (checkMacExcludedVersionOther && isOtherExcludeMajor) {
                    unsupportedOSVersion = true;
                }

                /// Checking the minimum version macOS requirement
                if (unsupportedOSVersion === false &&
                    (currentMajor > minimumMajor ||
                        (currentMajor === minimumMajor && currentMinor > minimumMinor) ||
                        (currentMajor === minimumMajor && currentMinor === minimumMinor && currentPatch >= minimumPatch))) {
                    osVersionCheckPass = true;
                }
            }

            setOSVersionCheckInProgress(false);
            setOSVersionCheckWarning(osVersionCheckWarning);

            if (osVersionCheckWarning === false) {
                if (osVersionCheckPass === true) {
                    setOSVersionCheckPassed(true);
                    //  setOSVersionCheckFailed(true);
                    appInsights.logInfo('OS System Check Passed.');
                }
                else {
                    setOSVersionCheckFailed(true);
                    setOSVersionCheckErrorMessage('Operating System version is not in the required range.');
                    appInsights.logInfo('OS System Check Failed.');
                }
            }

            appInsights.logInfo('OS System Check Completed.');
        } catch (ex: any) {
            setOSVersionCheckInProgress(false);
            setOSVersionCheckFailed(true);
            setOSVersionCheckWarning(false);
            setOSVersionCheckErrorMessage(ex.message);
        }
    };

    const checkScreenResolution = async () => {
        try {
            setScreenResolutionCheckNotStarted(false);
            setScreenResolutionCheckInProgress(true);  

            let screenResolutionMinimumWidth = parseInt(process.env.REACT_APP_SCREEN_RESOLUTION_MIN_WIDTH as string);
            let screenResolutionMinimumHeight = parseInt(process.env.REACT_APP_SCREEN_RESOLUTION_MIN_HEIGHT as string);
            let screenResolutionRecommendedWidth = parseInt(process.env.REACT_APP_SCREEN_RESOLUTION_RECOMMENDED_WIDTH as string);
            let screenResolutionRecommendedHeight = parseInt(process.env.REACT_APP_SCREEN_RESOLUTION_RECOMMENDED_HEIGHT as string);

            let minimumScreenResultion = screenResolutionMinimumWidth + 'px x ' + screenResolutionMinimumHeight + 'px';
            setMinimumScreenResolution(minimumScreenResultion);

            let recommendedScreenResultion = screenResolutionRecommendedWidth + 'px x ' + screenResolutionRecommendedHeight + 'px';
            setRecommendedScreenResolution(recommendedScreenResultion);

            await timeout(delay);

            let currentScreenResolutionWidth = window.screen.width;
            let currentScreenResolutionHeight = window.screen.height;
            let currentScreenResolution = currentScreenResolutionWidth + 'px x ' + currentScreenResolutionHeight + 'px';

            setCurrentScreenResolution(currentScreenResolution);

            setScreenResolutionCheckInProgress(false);

            if (currentScreenResolutionWidth < screenResolutionMinimumWidth
                || currentScreenResolutionHeight < screenResolutionMinimumHeight) {
                setScreenResolutionCheckFailed(true);
                setScreenResolutionCheckErrorMessage('Screen Resolution is lower than the minimum required resolution.');
            }
            else {
                setScreenResolutionCheckPassed(true);
            }
        } catch (ex: any) {
            setScreenResolutionCheckInProgress(false);
            setScreenResolutionCheckFailed(true);
            setScreenResolutionCheckErrorMessage(ex.message);
        }
    };

    const checkMicrophone = async () => {
        try {
            setMicrophoneCheckNotStarted(false);
            setMicrophoneCheckInProgress(true);
            await timeout(delay);

            if (!(navigator && navigator.mediaDevices && await navigator.mediaDevices.getUserMedia({ audio: true }) && navigator.mediaDevices.enumerateDevices)) {
                setMicrophoneCheckInProgress(false);
                setMicrophoneCheckFailed(true);
            }
            else {
                const streamData = await navigator.mediaDevices.getUserMedia({
                    audio: true
                });

                setAudioStream(streamData);

                const microphones: Array<IMicrophone> = [];
                navigator.mediaDevices.enumerateDevices()
                    .then((devices) => {
                        devices.forEach((device) => {
                            if (device.kind === 'audioinput' && isDeviceMicrophone(device)) {
                                microphones.push({
                                    label: device.label,
                                    id: device.deviceId
                                })
                            }
                        });

                        setAvailableMicrophones(microphones);

                        if (microphones.length === 0) {
                            setMicrophoneCheckInProgress(false);
                            setMicrophoneCheckFailed(true);
                            setDisplayMicrophoneCheckErrorMessage(true);
                            setMicrophoneCheckErrorMessage('No microphone found.');
                        }
                        else if (microphones.length > 1) {
                            setMultipleMicrophonesAvailable(true);
                        }

                        setSelectedMicrophoneName(microphones[0].label);
                        setEnableMicrophoneRecordOption(true);
                    });
            }
        } catch (ex: any) {
            setMicrophoneCheckInProgress(false);
            setMicrophoneCheckFailed(true);
            setDisplayMicrophoneCheckErrorMessage(true);
            setEnableMicrophoneSelectOption(true);
            setMicrophoneCheckErrorMessage(ex.message);
        }
    }

    const webcamCheckResult = async (resolution: string,
        name: string,
        errorMessage: string) => {
        setWebcamCurrentResolution(resolution);
        setWebcamName(name);
        setWebcamCheckErrorMessage(errorMessage);

        await checkDownloadSpeed();
        await timeout(delay);
        await checkUploadSpeed();
    }

    const checkDownloadSpeed = async () => {
        try {
            setDownloadSpeedCheckNotStarted(false);
            setDownloadSpeedCheckInProgress(true);

            let recommendedDownloadSpeedInMbps = parseFloat(process.env.REACT_APP_DOWNLOAD_SPEED_MINIMUM_IN_MBPS as string);
            setRecommendedDownloadSpeed(`${recommendedDownloadSpeedInMbps} Mbps`);
            let downloadSpeed = 0.0;
            setCurrentDownloadSpeed(`${downloadSpeed.toString()} Mbps`);

            await timeout(delay);

            downloadSpeed = parseFloat(await new DownloadSpeed().getDownloadSpeed());
            setCurrentDownloadSpeed(`${downloadSpeed.toString()} Mbps`);

            if (recommendedDownloadSpeedInMbps <= downloadSpeed) {
                setDownloadSpeedCheckPassed(true);
            } else {
                setDownloadSpeedCheckFailed(true);
                setDownloadSpeedCheckErrorMessage("Download Speed is less than the recommended value.");
            }

            setDownloadSpeedCheckInProgress(false);
        } catch (ex: any) {
            setDownloadSpeedCheckInProgress(false);
            setDownloadSpeedCheckFailed(true);
            setDownloadSpeedCheckErrorMessage(ex.message);
        }
    }

    const checkUploadSpeed = async () => {
        try {
            setUploadSpeedCheckNotStarted(false);
            setUploadSpeedCheckInProgress(true);

            let recommendedUploadSpeedInMbps = parseFloat(process.env.REACT_APP_UPLOAD_SPEED_MINIMUM_IN_MBPS as string);
            setRecommendedUploadSpeed(`${recommendedUploadSpeedInMbps} Mbps`);
            let uploadSpeed = 0.0;
            setCurrentUploadSpeed(`${uploadSpeed.toString()} Mbps`);

            await timeout(delay);

            let currentUploadSpeed = await new UploadSpeed().getUploadSpeed();

            if (currentUploadSpeed !== null) {
                uploadSpeed = parseFloat(currentUploadSpeed);
                setCurrentUploadSpeed(`${uploadSpeed.toString()} Mbps`);
            }

            if (recommendedUploadSpeedInMbps <= uploadSpeed) {
                setUploadSpeedCheckPassed(true);
            } else {
                setUploadSpeedCheckFailed(true);
                setUploadSpeedCheckErrorMessage("Upload Speed is less than the recommended value.");
            }

            setUploadSpeedCheckInProgress(false);
        } catch (ex: any) {
            setUploadSpeedCheckInProgress(false);
            setUploadSpeedCheckFailed(true);
            setUploadSpeedCheckErrorMessage(ex.message);
        }
    }

    const startRecording = () => {
        try {
            setEnableMicrophoneRecordOption(false);
            setEnableMicrophoneStopOption(true);
            setEnableMicrophonePlaybackOption(false);
            setEnableMicrophoneSelectOption(false);
            setDisplayMicrophoneCheckErrorMessage(false);

            const options = {
                mimeType: isMacOs && isSafari ? mimeTypeMp4 : mimeTypeWebm
            };

            const media = new MediaRecorder(audioStream, options);

            mediaRecorder.current = media;
            mediaRecorder.current.start();
            let localAudioChunks: any[] = [];
            mediaRecorder.current.ondataavailable = (event: { data: { size: number; }; }) => {
                if (typeof event.data === "undefined") return;
                if (event.data.size === 0) return;
                localAudioChunks.push(event.data);
            };

            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            var buffer = analyser.frequencyBinCount;
            analyser.fftSize = 1024;
            const audioSrc = audioContext.createMediaStreamSource(audioStream);
            audioSrc.connect(analyser);;
            const data = new Uint8Array(analyser.frequencyBinCount);

            AudioData.analyzer = analyser;
            AudioData.bufferLength = buffer;
            AudioData.dataArray = data;
            setAnalyzerData(AudioData);
            setAudioChunks(localAudioChunks);
            setPlayAudio(false);
        } catch (ex: any) {
            setEnableMicrophoneRecordOption(true);
            setEnableMicrophoneStopOption(false);
            setEnableMicrophonePlaybackOption(false);
            setEnableMicrophoneSelectOption(false);
            setDisplayMicrophoneCheckErrorMessage(true);
            setMicrophoneCheckErrorMessage(ex.message);
        }
    };

    const stopRecording = async () => {
        setAnalyzerData(null);
        setEnableMicrophoneRecordOption(true);
        setEnableMicrophoneStopOption(false);
        setEnableMicrophonePlaybackOption(true);
        setEnableMicrophoneSelectOption(false);

        mediaRecorder.current.stop();
        mediaRecorder.current.onstop = () => {
            const audioBlob = new Blob(audioChunks, { type: isMacOs && isSafari ? mimeTypeMp4 : mimeTypeWebm });
            const audioUrl = URL.createObjectURL(audioBlob);
            setAudio(audioUrl);
            setAudioChunks([]);
        };
    };

    const playRecording = () => {
        setPlayAudio(true);
        setEnableMicrophoneRecordOption(true);
        setEnableMicrophoneStopOption(false);
        setEnableMicrophonePlaybackOption(true);

        let audioPlayer = document.getElementById('audio') as HTMLAudioElement;
        let analyzer: AnalyserNode
        let bufferLength: number;
        let dataArray: Uint8Array
        audioPlayer.play();

        if (source) {
            analyzer = audioCtx.createAnalyser();
            bufferLength = analyzer.frequencyBinCount;
            dataArray = new Uint8Array(bufferLength);
            source.connect(analyzer);
            source.connect(audioCtx.destination);
            AudioData.dataArray = dataArray;
            AudioData.analyzer = analyzer;
            AudioData.bufferLength = bufferLength;
        } else {
            audioCtx = new (window.AudioContext)();
            analyzer = audioCtx.createAnalyser();
            analyzer.fftSize = 2048;
            bufferLength = analyzer.frequencyBinCount;
            dataArray = new Uint8Array(bufferLength);
            source = audioCtx.createMediaElementSource(audioElmRef.current);
            source.connect(analyzer);
            source.connect(audioCtx.destination);
            AudioData.analyzer = analyzer;
            AudioData.bufferLength = bufferLength;
            AudioData.dataArray = dataArray;
        }

        setAnalyzerData(AudioData);
        if (audio !== null) {
            setEnableMicrophoneSelectOption(true);
        }
    };

    const onMicrophoneSelectionChange = async (event: any) => {
        var selectedMicID = event.target.value;
        var constraints = { audio: { deviceId: selectedMicID } };

        const streamData = await navigator.mediaDevices.getUserMedia(constraints);
        setAudioStream(streamData);

        availableMicrophones?.forEach((microphone) => {
            if (microphone.id === selectedMicID) {
                setSelectedMicrophoneName(microphone.label);
            }
        });
    }

    const selectMicrophone = () => {
        if (microphoneCheckFailed) {
            setMicrophoneCheckPassed(false)
        } else {
            setMicrophoneCheckPassed(true);
        }
        setMicrophoneCheckInProgress(false);
        setDisplayMicrophoneCheckErrorMessage(false);
        setEnableMicrophoneSelectOption(true);
    }

    const proceedNextMicrophone = () => {
        setMicrophoneCheckComplete(true);
        audioStream?.getAudioTracks().forEach((audio: any) => audio.stop());
        checkWebcam();
    }

    const isDeviceMicrophone = (device: any) => {
        if (device.label.toLowerCase().indexOf('default') > -1
            || device.label.toLowerCase().indexOf('communications') > -1) {
            return false;
        }

        return true;
    }

    const getMicrophoneErrorDisplay = () => {
        { availableMicrophones === null || availableMicrophones?.length === 0 ? t('SYSTEM_CHECK_MICROPHONE_NOT_DETECTED') : t('SYSTEM_CHECK_MICROPHONE_ERROR_MESSAGE') }
        return (
            <section id='microphone-error-display'>
                <div className='microphone-error-display-left'>
                    <img className='webcam-exclamation-icon' src={OctagonExclamation} aria-hidden="true"  alt="" />
                </div>
                <div className='microphone-error-display-right'>
                    <span className='microphone-error-display-right-item'>{t('SYSTEM_CHECK_MICROPHONE_NOT_FOUND')}</span>
                    <span className='microphone-error-display-right-item'>{t('SYSTEM_CHECK_MICROPHONE_ERROR_MESSAGE')}</span>
                </div>
            </section>
        )
    }

    const getOSVersionCheckWarningDisplay = () => {
        return (
            <section id='os-warning-display'>
                <div className='os-warning-display-left'>
                    <i className='fa-solid fa-triangle-exclamation fa-lg'></i>
                </div>
                <div className='os-warning-display-right'>
                    <span className='os-warning-display-right-item'>{t('SYSTEM_CHECK_OPERATING_SYSTEM_WARNING_MESSAGE')}</span>
                </div>
            </section>
        )
    }

    const getErrorDisplay = (title: string, message: string) => {
        return (
            <section id='system-check-error-display'>
                <div className='system-check-error-display-left'>
                    <img className='system-check-exclamation-icon' src={OctagonExclamation} aria-hidden="true"  alt="" />
                </div>
                <div className='system-check-error-display-right'>
                    <span className='system-check-error-display-right-item'>{title}</span>
                    <span className='system-check-display-right-item'>{message}</span>
                </div>
            </section>
        )
    }

    const resetStates = () => {
        setOSVersionCheckNotStarted(true);
        setOSVersionCheckInProgress(false);
        setOSVersionCheckPassed(false);
        setOSVersionCheckFailed(false);
        setOSVersionCheckWarning(false);

        setScreenResolutionCheckNotStarted(true);
        setScreenResolutionCheckInProgress(false);
        setScreenResolutionCheckPassed(false);
        setScreenResolutionCheckFailed(false);
        setMicrophoneCheckNotStarted(true);
        setMicrophoneCheckInProgress(false);
        setMicrophoneCheckPassed(false);
        setMicrophoneCheckFailed(false);
        setEnableMicrophoneRecordOption(false);
        setEnableMicrophoneStopOption(false);
        setEnableMicrophonePlaybackOption(false);
        setEnableMicrophoneSelectOption(false);
        setDisplayMicrophoneCheckErrorMessage(false);
        setMicrophoneCheckComplete(false);

        setDownloadSpeedCheckNotStarted(true);
        setDownloadSpeedCheckInProgress(false);
        setDownloadSpeedCheckPassed(false);
        setDownloadSpeedCheckFailed(false);
        setUploadSpeedCheckNotStarted(true);
        setUploadSpeedCheckInProgress(false);
        setUploadSpeedCheckPassed(false);
        setUploadSpeedCheckFailed(false);
        setBrowserCheckStatus({
            notStarted: true,
            inProgress: false,
            passed: false,
            failed: false,
            completed: false,
        });

        setSystemCheckDone(false);

        resetErrorMessageStates()
    }

    const resetWebcamStatus = () => {
        WebcamStatus.notStarted = true;
        WebcamStatus.inProgress = false;
        WebcamStatus.failed = false;
        WebcamStatus.passed = false;
        webcamStatus.completed = false;

        setWebcamStatus(WebcamStatus);
    }

    const resetErrorMessageStates = () => {
        setOSVersionCheckErrorMessage('');
        setScreenResolutionCheckErrorMessage('');
        setMicrophoneCheckErrorMessage('');
        setWebcamCheckErrorMessage('');
        setDownloadSpeedCheckErrorMessage('');
        setUploadSpeedCheckErrorMessage('');
    }

    const logSystemCheckResult = () => {
        const operatingSystem: Result.OperatingSystem = {
            minimum: minimumOSVersion,
            current: currentOSVersion,
            status: osVersionCheckNotStarted ? SystemCheckStatus.NOT_STARTED :
                (osVersionCheckInProgress ? SystemCheckStatus.IN_PROGRESS :
                    (osVersionCheckPassed ? SystemCheckStatus.PASSED :
                        (osVersionCheckFailed ? SystemCheckStatus.FAILED :
                            (osVersionCheckWarning ? SystemCheckStatus.WARNING : SystemCheckStatus.UNKNOWN)))),
            error: osVersionCheckErrorMessage
        };

        const screenResolution: Result.ScreenResolution = {
            minimum: minimumScreenResolution,
            recommended: recommendedScreenResolution,
            current: currentScreenResolution,
            status: screenResolutionCheckNotStarted ? SystemCheckStatus.NOT_STARTED :
                (screenResolutionCheckInProgress ? SystemCheckStatus.IN_PROGRESS :
                    (screenResolutionCheckPassed ? SystemCheckStatus.PASSED :
                        (screenResolutionCheckFailed ? SystemCheckStatus.FAILED : SystemCheckStatus.UNKNOWN))),
            error: screenResolutionCheckErrorMessage
        };

        const microphone: Result.MediaDevice = {
            name: selectedMicrophoneName,
            status: microphoneCheckNotStarted ? SystemCheckStatus.NOT_STARTED :
                (microphoneCheckInProgress ? SystemCheckStatus.IN_PROGRESS :
                    (microphoneCheckPassed ? SystemCheckStatus.PASSED :
                        (microphoneCheckFailed ? SystemCheckStatus.FAILED : SystemCheckStatus.UNKNOWN))),
            error: microphoneCheckErrorMessage
        };

        const webcam: Result.Webcam = {
            recommended: process.env.REACT_APP_CAMERA_RESOLUTION_RECOMMENDED as string,
            current: webcamCurrentResolution,
            name: webcamName,
            status: webcamStatus.notStarted ? SystemCheckStatus.NOT_STARTED :
                (webcamStatus.inProgress ? SystemCheckStatus.IN_PROGRESS :
                    (webcamStatus.passed ? SystemCheckStatus.PASSED :
                        (webcamStatus.failed ? SystemCheckStatus.FAILED : SystemCheckStatus.UNKNOWN))),
            error: webcamCheckErrorMessage
        };

        const downloadSpeed: Result.Speed = {
            recommended: recommendedDownloadSpeed,
            current: currentDownloadSpeed,
            status: downloadSpeedCheckNotStarted ? SystemCheckStatus.NOT_STARTED :
                (downloadSpeedCheckInProgress ? SystemCheckStatus.IN_PROGRESS :
                    (downloadSpeedCheckPassed ? SystemCheckStatus.PASSED :
                        (downloadSpeedCheckFailed ? SystemCheckStatus.FAILED : SystemCheckStatus.UNKNOWN))),
            error: downloadSpeedCheckErrorMessage
        };

        const uploadSpeed: Result.Speed = {
            recommended: recommendedUploadSpeed,
            current: currentUploadSpeed,
            status: uploadSpeedCheckNotStarted ? SystemCheckStatus.NOT_STARTED :
                (uploadSpeedCheckInProgress ? SystemCheckStatus.IN_PROGRESS :
                    (uploadSpeedCheckPassed ? SystemCheckStatus.PASSED :
                        (uploadSpeedCheckFailed ? SystemCheckStatus.FAILED : SystemCheckStatus.UNKNOWN))),
            error: uploadSpeedCheckErrorMessage
        };

        const result: Result.Result = {
            operatingSystem: operatingSystem,
            screenResolution: screenResolution,
            microphone: microphone,
            webcam: webcam,
            downloadSpeed: downloadSpeed,
            uploadSpeed: uploadSpeed
        }

        const systemCheckResult: Result.SystemCheck = {
            status: (osVersionCheckPassed && screenResolutionCheckPassed && microphoneCheckPassed
                && WebcamStatus.passed && downloadSpeedCheckPassed && uploadSpeedCheckPassed) ? SystemCheckStatus.PASSED :
                (osVersionCheckWarning ? SystemCheckStatus.WARNING : SystemCheckStatus.FAILED),
            result: result
        }

        if(props.displaySystemCheckResults) {

            props.displaySystemCheckResults(systemCheckResult.status, systemCheckStart);
        }
        appInsights.logInfo(JSON.stringify({ 'systemCheck': systemCheckResult }));
    }

    const onModalClose = () => {
        logSystemCheckResult();

        resetStates();
        resetWebcamStatus();
        setSystemCheckStart(false)
        props.close();
    }

    const focusSelectMicrophone = (sel: any) => {
        if (multipleMicrophonesAvailable && !enableMicrophoneStopOption && !enableMicrophonePlaybackOption) {
            setTimeout(() => sel?.focus(), delay);
        }
    }

    const modelTitle = <h2>{t('SYSTEM_CHECK_TITLE')}</h2>
    const knowledgeBaseUrl= process.env.REACT_APP_KNOWLEDGE_BASE_URL as string;
    return (
        <>
            <Modal
                title={modelTitle}
                open={props.show}
                onCancel={onModalClose}
                cancelText={t('SYSTEM_CHECK_CANCEL')}
                okButtonProps={{ style: { display: 'none' } }}
                cancelButtonProps={{ style: { display: 'none' } }}
                className='system-check-modal'
                closable={false}
                destroyOnClose={true}
                width='860px'>
                <Row className='system-check-header'>
                    <Col span={13}>
                        <span className='system-check-readiness-message'>{t('SYSTEM_CHECK_READINESS_MESSAGE')}</span>
                        <span className='system-check-readiness-message'>{t('SYSTEM_CHECK_STEPS_MESSAGE')}</span>
                    </Col>
                    <Col span={2}></Col>
                    <Col span={9}>
                        <div className='start-system-check'>
                            <ProButton
                                text={t('SYSTEM_CHECK_START')}
                                colorscheme='secondary'
                                size='medium'
                                icon='arrow-right'
                                iconType='fa-solid'
                                classname='start-system-check-button'
                                iconPosition='left'
                                download=''
                                downloadUrl='#'
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.target.blur();
                                    startSystemCheck();
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                <div className="main-info-text">
                   <Trans
                        i18nKey='SYSTEM_CHECK_VPN_MESSAGE'
                        components={[<a className='main-text-link' href={knowledgeBaseUrl} target="_blank" />]}></Trans> 
                </div>
                <ul className='system-check'>
                    {
                        props.isCheckBrowser ? (
                            <li>
                               <Browser browserCheckStatus={browserCheckStatus} setBrowserCheckStatus={setBrowserCheckStatus}></Browser>
                            </li>
                        ) : (
                            <li>
                                <Row>
                                    <Col span={1}>
                                        <div className={osVersionCheckInProgress ? 'system-check-circle-in-progress' : 'system-check-circle'}>1</div>
                                        <div className='screen-readers-only'>Step 1 of 6</div>
                                    </Col>
                                    <Col span={3} className='system-check-icon with-margin-bottom-24px'>
                                        <i className='fa-solid fa-computer fa-2xl' aria-hidden='true'></i>
                                    </Col>
                                    <Col span={16}>
                                        <h3 data-testid="operating-system" className='system-check-name'>{t('SYSTEM_CHECK_OPERATING_SYSTEM')}</h3>
                                    </Col>
                                    <Col span={4}>
                                        <SystemCheckProgress
                                            name={t('SYSTEM_CHECK_OPERATING_SYSTEM')}
                                            value={currentOSVersion}
                                            notStarted={osVersionCheckNotStarted}
                                            inProgress={osVersionCheckInProgress}
                                            passed={osVersionCheckPassed}
                                            failed={osVersionCheckFailed}
                                            warning={osVersionCheckWarning}
                                        />
                                    </Col>

                                    <Col span={5} offset={2} className={osVersionCheckInProgress || osVersionCheckPassed || osVersionCheckFailed || osVersionCheckWarning ? 'system-check-result-show' : 'system-check-result-hide'}>
                                        <div className={osVersionCheckPassed || osVersionCheckFailed || osVersionCheckWarning ? 'system-check-result-show' : 'system-check-result-hide'}>
                                            <b>{t('SYSTEM_CHECK_YOUR_SYSTEM')}:</b>
                                        </div>
                                        <div className={osVersionCheckInProgress || osVersionCheckFailed || osVersionCheckWarning ? 'system-check-result-show' : 'system-check-result-hide'}>
                                            <div className={osVersionCheckWarning || osVersionCheckFailed ? 'system-check-padding-top-90' : ''}><b>{t('SYSTEM_CHECK_MINIMUM')}:</b></div>
                                        </div>
                                    </Col>
                                    <Col span={14} className={osVersionCheckInProgress || osVersionCheckPassed || osVersionCheckFailed || osVersionCheckWarning ? 'system-check-result-show' : 'system-check-result-hide'}>
                                        <div className={osVersionCheckPassed || osVersionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                            {osVersionCheckPassed? currentOSVersion : null }
                                        </div>

                                        {osVersionCheckWarning && getOSVersionCheckWarningDisplay()}
                                        {osVersionCheckFailed  && getErrorDisplay(t(''), t('SYSTEM_CHECK_YOUR_SYSTEM_FAILED'))}

                                        <div className={osVersionCheckInProgress || osVersionCheckFailed || osVersionCheckWarning ? 'system-check-result-show' : 'system-check-result-hide'}>
                                            <span className={osVersionCheckWarning ? 'system-check-padding-top-5 system-check-min-os-version' : 'system-check-min-os-version'}>{minimumOSVersion}</span>
                                        </div>
                                    </Col>
                                    <Col span={3}></Col>
                                </Row>
                            </li>
                        )
                    }
                    <li>
                        <Row>
                            <Col span={1}>
                                <div className={screenResolutionCheckInProgress ? 'system-check-circle-in-progress' : 'system-check-circle'}>2</div>
                                <div className='screen-readers-only'>Step 2 of 6</div>
                            </Col>
                            <Col span={3} className=' system-check-icon with-margin-bottom-24px'>
                                <i className='fa-solid fa-display fa-2xl' aria-hidden='true'></i>
                            </Col>
                            <Col span={16}>
                                <h3 className='system-check-name'>{t('SYSTEM_CHECK_SCREEN_RESOLUTION')}</h3>
                            </Col>
                            <Col span={4}>
                                <SystemCheckProgress
                                    name={t('SYSTEM_CHECK_SCREEN_RESOLUTION')}
                                    value={currentScreenResolution}
                                    notStarted={screenResolutionCheckNotStarted}
                                    inProgress={screenResolutionCheckInProgress}
                                    passed={screenResolutionCheckPassed}
                                    failed={screenResolutionCheckFailed}
                                />
                            </Col>

                            <Col span={5} offset={2} className={screenResolutionCheckInProgress || screenResolutionCheckPassed || screenResolutionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={screenResolutionCheckPassed || screenResolutionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <b>{t('SYSTEM_CHECK_YOUR_SYSTEM')}:</b>
                                </div>
                                <div className={screenResolutionCheckInProgress || screenResolutionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div><b>{t('SYSTEM_CHECK_MINIMUM')}:</b></div>
                                    <div><b>{t('SYSTEM_CHECK_RECOMMENDED')}:</b></div>
                                    <div><b>{t('SYSTEM_CHECK_MORE_INFORMATION')}:</b></div>
                                </div>
                            </Col>
                            <Col span={14} className={screenResolutionCheckInProgress || screenResolutionCheckPassed || screenResolutionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={screenResolutionCheckPassed || screenResolutionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    {currentScreenResolution}
                                </div>
                                <div className={screenResolutionCheckInProgress || screenResolutionCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div>{minimumScreenResolution}</div>
                                    <div>{recommendedScreenResolution}</div>
                                    <div>{t('SYSTEM_CHECK_SCREEN_RESOLUTION_MORE_INFORMATION')}</div>
                                </div>
                            </Col>
                            <Col span={3}></Col>
                        </Row>
                    </li>
                    <li>
                        <Row>
                            <Col span={1}>
                                <div className={microphoneCheckInProgress ? 'system-check-circle-in-progress' : 'system-check-circle'}>3</div>
                                <div className='screen-readers-only'>Step 3 of 6</div>
                            </Col>
                            <Col span={3} className='system-check-icon with-margin-bottom-24px'>
                                <i className='fa-solid fa-microphone fa-2xl' aria-hidden='true'></i>
                            </Col>
                            <Col span={16}>
                                <h3 className='system-check-name'>{t('SYSTEM_CHECK_MICROPHONE')}</h3>
                            </Col>
                            <Col span={4}>
                                <SystemCheckProgress
                                    name={t('SYSTEM_CHECK_MICROPHONE')}
                                    notStarted={microphoneCheckNotStarted}
                                    inProgress={microphoneCheckInProgress}
                                    passed={microphoneCheckPassed}
                                    failed={microphoneCheckFailed}
                                />
                            </Col>
                            <Col span={5} offset={2} className={microphoneCheckInProgress || microphoneCheckPassed || microphoneCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={microphoneCheckInProgress || microphoneCheckPassed || displayMicrophoneCheckErrorMessage || microphoneCheckComplete ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div><b>{t('SYSTEM_CHECK_SELECTED_MICROPHONE')}:</b></div>
                                </div>
                            </Col>
                            <Col span={14} className={microphoneCheckInProgress || microphoneCheckPassed || microphoneCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={microphoneCheckInProgress || microphoneCheckPassed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div className={microphoneCheckInProgress && multipleMicrophonesAvailable ? 'system-check-result-show' : 'system-check-result-hide'}>
                                        <label htmlFor='select-microphone'>{t('SYSTEM_CHECK_SELECT_THE_DESIRED_MICROPHONE')}</label>
                                        <div>
                                            <select id='select-microphone' className='system-check-microphone-select' onChange={onMicrophoneSelectionChange} ref={focusSelectMicrophone}>
                                                {availableMicrophones?.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className={(!multipleMicrophonesAvailable || (multipleMicrophonesAvailable && microphoneCheckPassed)) ? 'system-check-result-show with-padding-left-8px with-padding-right-8px' : 'system-check-result-hide'}>
                                        {selectedMicrophoneName}
                                    </div>
                                </div>
                                {displayMicrophoneCheckErrorMessage && getMicrophoneErrorDisplay()}
                            </Col>
                            <Col span={5} offset={2} className={(microphoneCheckInProgress || microphoneCheckPassed || microphoneCheckFailed ? 'system-check-result-show' : 'system-check-result-hide') + ' new-row'}>
                                <div className={microphoneCheckInProgress ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div><b>{t('SYSTEM_CHECK_MICROPHONE_RECORD_AND_TEST')}:</b></div>
                                </div>
                            </Col>
                            <Col span={14} className={(microphoneCheckInProgress || microphoneCheckPassed || microphoneCheckFailed ? 'system-check-result-show' : 'system-check-result-hide') + ' new-row'}>
                                <div className={microphoneCheckInProgress || microphoneCheckPassed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div className={(microphoneCheckInProgress || !microphoneCheckPassed) ? 'system-check-result-show' : 'system-check-result-hide'}>

                                        <div>{t('SYSTEM_CHECK_MICROPHONE_RECORD_AND_TEST_MESSAGE')}</div>

                                        {analyzerData ? <WaveForm analyzerData={analyzerData} /> : <div className='system-check-microphone-visualizer-frame'> </div>}


                                        <div className='system-check-microphone-button-group'>
                                            <ProButton
                                                isFocus={enableMicrophoneRecordOption && !multipleMicrophonesAvailable}
                                                text={t('SYSTEM_CHECK_MICROPHONE_RECORD')}
                                                colorscheme='secondary'
                                                size='small'
                                                icon='microphone'
                                                iconType='fa-solid'
                                                classname={(enableMicrophoneRecordOption ? 'system-check-microphone-button-main' : 'disabled') + ' fullwidth check system-check-microphone-button'}
                                                iconPosition='left'
                                                download=''
                                                downloadUrl='#'
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.target.blur();
                                                    if (enableMicrophoneRecordOption) {
                                                        startRecording();
                                                    }
                                                }}
                                            />
                                            <ProButton
                                                isFocus={enableMicrophoneStopOption}
                                                text={t('SYSTEM_CHECK_MICROPHONE_STOP')}
                                                colorscheme='secondary'
                                                size='small'
                                                icon='stop'
                                                iconType='fa-solid'
                                                classname={(enableMicrophoneStopOption ? 'system-check-microphone-button-main' : 'disabled') + ' fullwidth check system-check-microphone-button'}
                                                iconPosition='left'
                                                download=''
                                                downloadUrl='#'
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.target.blur();
                                                    if (enableMicrophoneStopOption) {
                                                        stopRecording();
                                                    }
                                                }}
                                            />
                                            <ProButton
                                                isFocus={enableMicrophonePlaybackOption}
                                                text={t('SYSTEM_CHECK_MICROPHONE_PLAYBACK')}
                                                colorscheme='secondary'
                                                size='small'
                                                icon='play'
                                                iconType='fa-solid'
                                                classname={(enableMicrophonePlaybackOption ? 'system-check-microphone-button-main' : 'disabled') + ' fullwidth check system-check-microphone-button'}
                                                iconPosition='left'
                                                download=''
                                                downloadUrl='#'
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.target.blur();
                                                    if (enableMicrophonePlaybackOption) {
                                                        playRecording();
                                                    }
                                                }}
                                            />
                                        </div>

                                        <audio id='audio' src={audio ?? ''} ref={audioElmRef} ></audio>

                                    </div>
                                </div>
                                <div className={microphoneCheckComplete ? 'system-check-result-hide' : 'system-check-microphone-button-group with-margin-top-12px'}>
                                    <ProButton
                                        isFocus={enableMicrophoneSelectOption}
                                        text={t('SYSTEM_CHECK_PROCEED_NEXT')}
                                        colorscheme='secondary'
                                        size='small'
                                        icon='arrow-right'
                                        iconType='fa-solid'
                                        classname={(enableMicrophoneSelectOption ? 'system-check-microphone-complete-button' : 'disabled')}
                                        iconPosition='left'
                                        download=''
                                        downloadUrl='#'
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.target.blur();
                                            if (enableMicrophoneSelectOption) {
                                                selectMicrophone();
                                                proceedNextMicrophone();
                                            }
                                        }}
                                    />
                                </div>
                            </Col>
                            <Col span={3}></Col>
                        </Row>
                    </li>
                    <li>
                        <Webcam webcamStatus={webcamStatus} setWebcamStatus={setWebcamStatus} webcamCheckResult={webcamCheckResult}></Webcam>
                        {!isMacOs && <div tabIndex={-1} aria-hidden='true' ref={(div) => div?.focus()}></div>}
                    </li>
                    <li>
                        <Row>
                            <Col span={1}>
                                <div className={downloadSpeedCheckInProgress ? 'system-check-circle-in-progress' : 'system-check-circle'}>5</div>
                                <div className='screen-readers-only'>Step 5 of 6</div>
                            </Col>
                            <Col span={3} className='system-check-icon with-margin-bottom-24px'>
                                <i className='fa-solid fa-download fa-2xl' aria-hidden='true'></i>
                            </Col>
                            <Col span={16}>
                                <h3 className='system-check-name'>{t('SYSTEM_CHECK_DOWNLOAD_SPEED')}</h3>
                            </Col>
                            <Col span={4}>
                                <SystemCheckProgress
                                    name={t('SYSTEM_CHECK_DOWNLOAD_SPEED')}
                                    value={currentDownloadSpeed}
                                    notStarted={downloadSpeedCheckNotStarted}
                                    inProgress={downloadSpeedCheckInProgress}
                                    passed={downloadSpeedCheckPassed}
                                    failed={downloadSpeedCheckFailed}
                                />
                            </Col>
                            <Col span={5} offset={2} className={downloadSpeedCheckInProgress || downloadSpeedCheckPassed || downloadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={downloadSpeedCheckPassed || downloadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div><b>{t('SYSTEM_CHECK_YOUR_SYSTEM')}:</b></div>
                                </div>
                                <div className={downloadSpeedCheckInProgress || downloadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div><b>{t('SYSTEM_CHECK_RECOMMENDED')}:</b></div>
                                </div>
                            </Col>
                            <Col span={14}
                                className={downloadSpeedCheckInProgress || downloadSpeedCheckPassed || downloadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={downloadSpeedCheckPassed || downloadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    {currentDownloadSpeed}
                                </div>
                                <div className={downloadSpeedCheckInProgress || downloadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    {recommendedDownloadSpeed}
                                </div>
                            </Col>
                            <Col span={3}></Col>
                        </Row>
                    </li>
                    <li>
                        <Row>
                            <Col span={1}>
                                <div className={uploadSpeedCheckInProgress ? 'system-check-circle-in-progress' : 'system-check-circle'}>6</div>
                                <div className='screen-readers-only'>Step 6 of 6</div>
                            </Col>
                            <Col span={3} className='system-check-icon with-margin-bottom-24px'>
                                <i className='fa-solid fa-upload fa-2xl' aria-hidden='true'></i>
                            </Col>
                            <Col span={16}>
                                <h3 className='system-check-name'>{t('SYSTEM_CHECK_UPLOAD_SPEED')}</h3>
                            </Col>
                            <Col span={4}>
                                <SystemCheckProgress
                                    name={t('SYSTEM_CHECK_UPLOAD_SPEED')}
                                    value={currentUploadSpeed}
                                    notStarted={uploadSpeedCheckNotStarted}
                                    inProgress={uploadSpeedCheckInProgress}
                                    passed={uploadSpeedCheckPassed}
                                    failed={uploadSpeedCheckFailed}
                                />
                            </Col>
                            <Col span={5} offset={2}
                                className={uploadSpeedCheckInProgress || uploadSpeedCheckPassed || uploadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={uploadSpeedCheckPassed || uploadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div> <b>{t('SYSTEM_CHECK_YOUR_SYSTEM')}:</b></div>
                                </div>
                                <div className={uploadSpeedCheckInProgress || uploadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    <div> <b>{t('SYSTEM_CHECK_RECOMMENDED')}:</b></div>
                                </div>
                            </Col>
                            <Col span={16}
                                className={uploadSpeedCheckInProgress || uploadSpeedCheckPassed || uploadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                <div className={uploadSpeedCheckPassed || uploadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    {currentUploadSpeed}
                                </div>
                                <div className={uploadSpeedCheckInProgress || uploadSpeedCheckFailed ? 'system-check-result-show' : 'system-check-result-hide'}>
                                    {recommendedUploadSpeed}
                                </div>
                            </Col>
                            <Col span={3}></Col>
                        </Row>
                    </li>
                    <li role='status' aria-live='polite'>
                        <Row className={(systemCheckDone && (osVersionCheckPassed || browserCheckStatus.passed) && screenResolutionCheckPassed && microphoneCheckPassed
                            && WebcamStatus.passed && downloadSpeedCheckPassed && uploadSpeedCheckPassed ? 'system-check-result-show' : 'system-check-result-hide')}>
                            <Col span={24}>
                                <div className='system-check-passed-result-show'>
                                    <i className='fa-solid fa-circle-check fa-2xl' aria-hidden='true'></i>
                                    <span className='system-check-final-result-message'>{t('SYSTEM_CHECK_ALL_PASSED')}</span>
                                </div>
                            </Col>
                        </Row>
                    </li>
                    <li role='status' aria-live='polite'>
                        <Row className={systemCheckDone && (osVersionCheckFailed || browserCheckStatus.failed || screenResolutionCheckFailed || microphoneCheckFailed
                            || WebcamStatus.failed || downloadSpeedCheckFailed || uploadSpeedCheckFailed) ? 'system-check-result-show' : 'system-check-result-hide'}>
                            <Col span={24}>
                                <div className='system-check-failed-result-show'>
                                    <img aria-hidden='true' className='failed-exclamation-icon' src={OctagonExclamation} alt="" />
                                    <span className='system-check-final-result-message'>{t('SYSTEM_CHECK_ALL_FAILED')}</span>
                                </div>
                            </Col>
                        </Row>
                    </li>
                    <li role='status' aria-live='polite'>
                        <Row className={(osVersionCheckWarning && screenResolutionCheckPassed && microphoneCheckPassed
                            && WebcamStatus.passed && downloadSpeedCheckPassed && uploadSpeedCheckPassed ? 'system-check-result-show' : 'system-check-result-hide')}>
                            <Col span={24}>
                                <div className='system-check-warning-result-show'>
                                    <i className='fa-solid fa-triangle-exclamation fa-2xl' aria-hidden='true'></i>
                                    <span className='system-check-final-result-message system-check-final-warning-message'>{t('SYSTEM_CHECK_ALL_WARNING')}</span>
                                </div>
                            </Col>
                        </Row>
                    </li>
                </ul>

                <Row className='system-check-footer'>
                    <Col span={15}>
                    </Col>
                    <Col span={9}>
                        <div className='cancel-system-check'>
                            <ProButton
                                isFocus={systemCheckDone && (((osVersionCheckPassed || browserCheckStatus.passed) && screenResolutionCheckPassed && microphoneCheckPassed
                                    && WebcamStatus.passed && downloadSpeedCheckPassed && uploadSpeedCheckPassed) || (osVersionCheckFailed || browserCheckStatus.failed || screenResolutionCheckFailed || microphoneCheckFailed
                                        || WebcamStatus.failed || downloadSpeedCheckFailed || uploadSpeedCheckFailed) || ((osVersionCheckWarning || browserCheckStatus.passed) && screenResolutionCheckPassed && microphoneCheckPassed
                                            && WebcamStatus.passed && downloadSpeedCheckPassed && uploadSpeedCheckPassed)) && (webcamStatus.completed && microphoneCheckComplete)}
                                delayFocus={delay}
                                text={t('SYSTEM_CHECK_CLOSE')}
                                colorscheme='secondary'
                                size='medium'
                                icon=''
                                iconType=''
                                classname='cancel-system-check-button'
                                iconPosition='left'
                                download=''
                                downloadUrl='#'
                                onClick={(event) => {
                                    event.preventDefault();
                                    onModalClose();
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </Modal>
        </>
    );
}

export default SystemCheck;